import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { UpdateAcMappingBody } from '../api/fieldMappings';
import {
  getActiveCampaignFieldMappings,
  updateActiveCampaignFieldMapping,
} from '../api/fieldMappings';
import type { ActiveCampaignFieldMappingsResponse } from '../types';
import { getAcMappingErrorMessage } from '../utils/mappingErrors';

interface UseActiveCampaignMappingsOptions {
  /**
   * Gate the field-mappings fetch. Field mappings only exist once the
   * integration is connected, so callers should pass `false` until then —
   * otherwise the query fetches prematurely, caches an empty/404 result,
   * and the UI shows "No field mappings found" even after they're created.
   * Defaults to true.
   */
  enabled?: boolean;
}

const EMPTY_SAVING_COUNTS: ReadonlyMap<number, number> = new Map();
const EMPTY_ERROR_MESSAGES: ReadonlyMap<number, string> = new Map();

export function useActiveCampaignMappings(
  orgId: number | string | undefined,
  options?: UseActiveCampaignMappingsOptions
) {
  const queryClient = useQueryClient();

  const mappingsQuery = useQuery<ActiveCampaignFieldMappingsResponse>({
    queryKey: ['activeCampaignMappings', orgId],
    queryFn: () => {
      if (!orgId) {
        throw new Error('orgId is required');
      }
      return getActiveCampaignFieldMappings(orgId);
    },
    enabled: !!orgId && (options?.enabled ?? true),
  });

  // Per-row save/error state, keyed by mappingId. React Query's useMutation keeps
  // a single observer that only retains the *latest* call's status/variables —
  // and its option-level lifecycle callbacks (onMutate/onError/onSettled) are
  // dropped for a call that a later mutate() supersedes. With overlapping edits
  // that would misattribute the spinner/error to whichever row was edited last,
  // or hide a real failure entirely. The one seam React Query runs exactly once
  // per mutate(), regardless of supersession, is mutationFn — so we track each
  // request there:
  //   - savingCounts ref-counts in-flight requests per row, so overlapping edits
  //     on the same row keep the spinner until the last one settles.
  //   - errorMessagesById holds the friendly error for each failed row until its
  //     next attempt starts (which clears it) or succeeds.
  //   - latestRequestSeqRef makes row error state last-write-wins: when two saves
  //     for the same row overlap, only the most recent request may set or clear
  //     that row's error, so an earlier failure can't linger after a later success
  //     (and a stale failure can't overwrite a newer success).
  //   - orgIdRef scopes all of the above to the org a request was fired under.
  //     Mapping ids are per-org and can collide, and the seq counter restarts per
  //     org, so without an org check a stale settlement from the previous org
  //     could bleed spinner/error state onto an identically-numbered row in the
  //     newly selected org.
  const [savingCounts, setSavingCounts] =
    useState<ReadonlyMap<number, number>>(EMPTY_SAVING_COUNTS);
  const [errorMessagesById, setErrorMessagesById] =
    useState<ReadonlyMap<number, string>>(EMPTY_ERROR_MESSAGES);
  const latestRequestSeqRef = useRef<Map<number, number>>(new Map());
  // Always holds the currently selected org so an in-flight request can tell
  // whether the user has since switched orgs (see the reset effect below).
  const orgIdRef = useRef(orgId);

  // Per-row mutation state is local to this hook and lives outside the
  // orgId-scoped query cache, so it must be cleared when orgId changes —
  // otherwise a newly selected org would show save/error indicators left over
  // from the previous org's rows (whose mappingIds are meaningless for the new
  // org). Resetting on orgId change gives each org a clean mapping status.
  useEffect(() => {
    orgIdRef.current = orgId;
    setSavingCounts(EMPTY_SAVING_COUNTS);
    setErrorMessagesById(EMPTY_ERROR_MESSAGES);
    latestRequestSeqRef.current = new Map();
  }, [orgId]);

  const updateMapping = useMutation({
    mutationFn: async ({
      mappingId,
      body,
    }: {
      mappingId: number;
      body: UpdateAcMappingBody;
    }) => {
      if (!orgId) {
        throw new Error('orgId is required');
      }
      // Claim latest-request ownership for this row so only this request may
      // update the row's error state once it settles (last-write-wins). Ownership
      // is scoped to the org this request was fired under: after an org switch the
      // reset effect above has already run, so a stale settlement from the previous
      // org fails the org check and cannot touch the new org's row state.
      const requestOrgId = orgId;
      const requestSeq = (latestRequestSeqRef.current.get(mappingId) ?? 0) + 1;
      latestRequestSeqRef.current.set(mappingId, requestSeq);
      const ownsRowState = () =>
        orgIdRef.current === requestOrgId &&
        latestRequestSeqRef.current.get(mappingId) === requestSeq;
      // Mark this row saving and clear any error left from a prior attempt.
      setSavingCounts(prev => {
        const next = new Map(prev);
        next.set(mappingId, (next.get(mappingId) ?? 0) + 1);
        return next;
      });
      setErrorMessagesById(prev => {
        if (!prev.has(mappingId)) {
          return prev;
        }
        const next = new Map(prev);
        next.delete(mappingId);
        return next;
      });
      try {
        const result = await updateActiveCampaignFieldMapping(
          orgId,
          mappingId,
          body
        );
        await queryClient.invalidateQueries({
          queryKey: ['activeCampaignMappings', orgId],
        });
        // Clear any stale error left by an earlier overlapping attempt on this
        // row, but only if this is still the row's most recent request on the
        // current org.
        if (ownsRowState()) {
          setErrorMessagesById(prev => {
            if (!prev.has(mappingId)) {
              return prev;
            }
            const next = new Map(prev);
            next.delete(mappingId);
            return next;
          });
        }
        return result;
      } catch (error) {
        // Surface the failure only if this is still the row's latest request on
        // the current org; a newer request that already resolved must not be
        // overwritten, and a request from a since-abandoned org must stay silent.
        if (ownsRowState()) {
          setErrorMessagesById(prev => {
            const next = new Map(prev);
            next.set(mappingId, getAcMappingErrorMessage(error));
            return next;
          });
        }
        throw error;
      } finally {
        // Only unwind the spinner while we're still on the request's org. After
        // an org switch the reset effect already cleared savingCounts, so
        // decrementing here would corrupt the new org's counts (or underflow a
        // row that happens to share this mapping id).
        if (orgIdRef.current === requestOrgId) {
          setSavingCounts(prev => {
            const current = prev.get(mappingId) ?? 0;
            const next = new Map(prev);
            if (current <= 1) {
              next.delete(mappingId);
            } else {
              next.set(mappingId, current - 1);
            }
            return next;
          });
        }
      }
    },
  });

  const savingIds = useMemo<ReadonlySet<number>>(
    () => new Set(savingCounts.keys()),
    [savingCounts]
  );

  return useMemo(
    () => ({
      mappings: mappingsQuery,
      isLoading: mappingsQuery.isLoading,
      error: mappingsQuery.error,
      updateMapping,
      savingIds,
      errorMessagesById,
    }),
    [mappingsQuery, updateMapping, savingIds, errorMessagesById]
  );
}
